import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import IconMail from '../../component/icon/IconMail'
import axios from 'axios'
import BlankLayout from '../../component/layout/BlankLayout'
import { toast } from 'react-toastify'
import Dropdown from '../../component/Dropdown'
import { languageList } from '../../util/helper'
import i18next from 'i18next'
import IconGlobe from '../../component/icon/IconGlobe'
import { useTranslation } from 'react-i18next'
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Helmet } from 'react-helmet'


const Register = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [showOtp, setShowOtp] = useState(false)
    const [loading, setLoading] = useState(false)
    const [timeLeft, setTimeLeft] = useState(0)

    // Function to start the timer and store timestamp
    const startTimer = () => {
        const now = Date.now()
        const expireTime = now + 60 * 1000 // 1-minute expiry
        localStorage.setItem("otp_timer", expireTime.toString())
        setTimeLeft(60)
    }

    // Effect to check the timer on mount
    useEffect(() => {
        const storedExpireTime = localStorage.getItem("otp_timer")
        if (storedExpireTime) {
            const remainingTime = Math.floor((parseInt(storedExpireTime) - Date.now()) / 1000)
            if (remainingTime > 0) {
                setTimeLeft(remainingTime)
            }
        }

        // Timer countdown
        const interval = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 1) {
                    clearInterval(interval)
                    localStorage.removeItem("otp_timer")
                    return 0
                }
                return prev - 1
            });
        }, 1000)

        return () => clearInterval(interval)
    }, [])

    const generateOtp = async (values:any) => {
        if (values.email.trim() === "") return toast.error(t('register.email_required'))

        setLoading(true)
        
        try {
            // First try to login with dummy password to check account status
            const loginResponse = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/user/login`, {
                email: values.email.trim(),
                password: "dummy_password", // dummy password
                role: "coach"
            })

            // If login succeeds unexpectedly, account exists - redirect to login
            setLoading(false)
            toast.error(t('register.account_exists'))
            setTimeout(() => {
                navigate('/login')
            }, 500)
            return
            
        } catch (loginError: any) {
            // If it's "Invalid credentials" error, account exists but password is wrong
            if (loginError.response?.data?.error === "Invalid credentials") {
                setLoading(false)
                toast.error(t('register.account_exists'))
                setTimeout(() => {
                    navigate('/login')
                }, 500)
                return
            }
            
            // For other errors (like user not found), proceed with OTP generation
            try {
                const otpResponse = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/user/generate-otp`, {
                    email: values.email.trim(),
                })
                
                console.log("OTP response:", otpResponse)
                setShowOtp(true)
                startTimer() // Start timer after OTP request
                toast.success(t('register.otp_sent'))
                
            } catch (otpError: any) {
                console.log("OTP error:", otpError)
                if (otpError.response?.data?.message) {
                    toast.error(otpError.response.data.message)
                } else {
                    toast.error(t("server_error"))
                }
            } finally {
                setLoading(false)
            }
        }
    }

    const checkExist = async (values:any) => {
        if (values.email.trim() === "" || values.otp.trim() === "") return toast.error(t('register.email_otp_required'))

        setLoading(true)
        axios.post(`${process.env.REACT_APP_API_BASE_URL}/user/exist`, {
            "email": values.email.trim(),
            "otp": values.otp.trim()
        }, {headers: {'Content-Type': 'application/json', "Accept":"application/json"}, withCredentials: true }).then( res => {
            setLoading(false)
            console.log("res check exist", res)
            if (res.data.is_exist === false) {
                navigate(`/register-new?email=${values.email}`)
            } else {
                navigate(`/register-exist?email=${values.email}`)
            }
        }).catch( err => {
            setLoading(false)
            console.log("err", err.response)
            toast.error("Error validating user account")
        })
    }


    const validationSchema = (showOtp: boolean) => 
        Yup.object().shape({
            email: Yup.string()
                .required(t("validation.required"))
                .test(
                    'no-trim-spaces',
                    t("validation.no_spaces_allowed"),
                    value => value === value?.trim()
                )
                .email(t("validation.invalid_email")),
            otp: showOtp ? Yup.string().required(t("validation.required")) : Yup.string(),
        })

    return (
        <>
        <Helmet>
            <title>Sign Up as Coach | MyKoku Platform Malaysia</title>
            <meta name="description" content="Create your free coach account on MyKoku. Manage students, track attendance, and schedule lessons – perfect for Malaysian tutors and coaching centers."/>
        </Helmet>
        <BlankLayout>
            <div className="relative flex min-h-screen items-center justify-center bg-cover bg-center bg-no-repeat px-6 py-10 dark:bg-[#060818] sm:px-16">
                <div className="relative w-full max-w-[870px] rounded-md bg-[linear-gradient(45deg,#fff9f9_0%,rgba(255,255,255,0)_25%,rgba(255,255,255,0)_75%,_#fff9f9_100%)] p-2 dark:bg-[linear-gradient(52.22deg,#0E1726_0%,rgba(14,23,38,0)_18.66%,rgba(14,23,38,0)_51.04%,rgba(14,23,38,0)_80.07%,#0E1726_100%)]">
                    <div className="relative flex flex-col justify-center rounded-md backdrop-blur-lg dark:bg-black/50 px-6 lg:min-h-[758px] py-20">
                        <div className="mx-auto w-full max-w-[440px]">
                            <div className="mb-10">
                                <img className="w-20" src="/assets/images/koku.png" alt="logo"/>
                                <h1 className="text-3xl font-extrabold uppercase !leading-snug text-primary md:text-4xl">{t('register.sign_up')}</h1>
                                <p className="text-base font-bold leading-normal text-white-dark">{t('register.enter_email_password')}</p>
                            </div>
                            <Formik
                                    initialValues={{ email: "", otp: "" }}
                                    validationSchema={validationSchema(showOtp)}
                                    onSubmit={(values) => {
                                        showOtp ? checkExist(values) : generateOtp(values)
                                    }}
                                >
                                    {({ isSubmitting }) => (
                                <Form>
                                    <div>
                                        <label htmlFor="email">{t("register.email")}</label>
                                        <Field type="text" name="email" id="email" placeholder={t("register.enter_email")} className="form-input placeholder:text-white-dark" />
                                        <ErrorMessage name="email" component="div" className="mt-1 text-red-500 text-sm" />
                                    </div>
                                    {showOtp && (
                                    <div className='mt-3'>
                                        <label htmlFor="otp">{t("register.otp")}</label>
                                        <Field type="text" name="otp" id="otp" placeholder={t("register.enter_otp")} className="form-input placeholder:text-white-dark" />
                                        <ErrorMessage name="otp" component="div" className="mt-1 text-red-500 text-sm" />
                                    </div>
                                    )}
                                    <button type="submit" className="btn btn-primary rounded-3xl !mt-6 w-full border-0 uppercase shadow-[0_10px_20px_-10px_rgba(67,97,238,0.44)]"  disabled={(!showOtp && loading) || (!showOtp && timeLeft > 0)}>
                                        {loading ? (
                                            <span className="animate-spin border-2 border-white dark:border-white !border-l-transparent rounded-full w-5 h-5 inline-flex"></span>
                                        ) : (
                                            showOtp ? t('register.verify_otp') : timeLeft > 0 ? `Resend OTP (${timeLeft}s)` : t("register.send_otp")
                                        )}
                                    </button>
                                </Form>
                                )}
                            </Formik>
                            <div className="relative my-7 text-center md:mb-9">
                                <span className="absolute inset-x-0 top-1/2 h-px w-full -translate-y-1/2 bg-white-light dark:bg-white-dark"></span>
                                <span className="relative bg-white px-2 font-bold uppercase text-white-dark dark:bg-dark dark:text-white-light">{t('register.or')}</span>
                            </div>
                            <div className="text-center dark:text-white">
                                {t('register.already_have_account')}&nbsp;
                                <Link to="/login" className="uppercase text-primary underline transition hover:text-black dark:hover:text-white">
                                    {t('register.sign_in')}
                                </Link>
                            </div>

                            <div>
                                <Dropdown placement={`bottom-start`} btnClassName=" p-2 rounded-full bg-white-light/40 dark:bg-dark/40 hover:text-primary hover:bg-white-light/90 dark:hover:bg-dark/60" button={<IconGlobe/>} >
                                    <ul className="p-2 text-dark dark:text-white-dark grid grid-cols-2 gap-2 font-semibold dark:text-white-light/90 w-[280px]">
                                        {languageList.map((item: any) => {
                                            return (
                                                <li key={item.code}>
                                                    <button
                                                        type="button"
                                                        className={`flex pt-2 w-full hover:text-primary rounded-lg ${i18next.language === item.code ? 'bg-primary/10 text-primary' : ''}`}
                                                        onClick={() => {
                                                            i18next.changeLanguage(item.code)
                                                            // setFlag(item.code);
                                                        }}
                                                    >
                                                        <img src={`/assets/images/flags/${item.code.toUpperCase()}.svg`} alt="flag" className="w-5 h-5 object-cover rounded-full" />
                                                        <span className="ltr:ml-3 rtl:mr-3">{item.name}</span>
                                                    </button>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </Dropdown>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </BlankLayout>
        </>
    )
}

export default Register